import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { ElevenLabsService } from '../services/elevenLabsService';
import { ElevenLabsStatus } from '../types';
import styles from './ServiceStatus.module.css';

interface ServiceStatusProps {
  onStatusChange?: (status: ElevenLabsStatus) => void;
  compact?: boolean;
}

export const ServiceStatus: React.FC<ServiceStatusProps> = ({ onStatusChange, compact = false }) => {
  const [status, setStatus] = useState<ElevenLabsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const serviceStatus = await ElevenLabsService.getStatus();
      setStatus(serviceStatus);
      onStatusChange?.(serviceStatus);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Failed to check ElevenLabs status:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (loading) return <FontAwesomeIcon icon={faSpinner} spin />;
    if (error) return <FontAwesomeIcon icon={faTimes} className={styles.errorIcon} />;
    if (!status) return <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warningIcon} />;
    if (status.enabled && status.configured) return <FontAwesomeIcon icon={faCheck} className={styles.successIcon} />;
    return <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warningIcon} />;
  };

  const getStatusMessage = () => {
    if (loading) return 'Checking ElevenLabs status...';
    if (error) return `Error: ${error}`;
    if (!status) return 'Unable to check status';
    if (status.enabled && status.configured) return `Ready (${status.voiceCount || 0} voices available)`;
    if (!status.enabled) return 'ElevenLabs is disabled';
    if (!status.configured) return 'ElevenLabs not configured';
    return 'Unknown status';
  };

  const getStatusColor = () => {
    if (loading) return 'blue';
    if (error) return 'red';
    if (!status) return 'orange';
    if (status.enabled && status.configured) return 'green';
    return 'orange';
  };

  if (compact) {
    return (
      <div className={`${styles.statusCompact} ${styles[getStatusColor()]}`}>
        {getStatusIcon()}
        <span className={styles.statusText}>{getStatusMessage()}</span>
      </div>
    );
  }

  return (
    <div className={`${styles.statusCard} ${styles[getStatusColor()]}`}>
      <div className={styles.statusHeader}>
        <div className={styles.statusIcon}>{getStatusIcon()}</div>
        <div className={styles.statusTitle}>ElevenLabs Service</div>
      </div>
      
      <div className={styles.statusMessage}>{getStatusMessage()}</div>
      
      {status && (
        <div className={styles.statusDetails}>
          <div className={styles.statusItem}>
            <span className={styles.statusLabel}>Model:</span>
            <span className={styles.statusValue}>{status.defaultModel}</span>
          </div>
          {status.voiceCount && (
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>Voices:</span>
              <span className={styles.statusValue}>{status.voiceCount}</span>
            </div>
          )}
          <div className={styles.statusItem}>
            <span className={styles.statusLabel}>API Key:</span>
            <span className={styles.statusValue}>{status.apiKey}</span>
          </div>
          {status.error && (
            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>Error:</span>
              <span className={styles.statusValue}>{status.error}</span>
            </div>
          )}
        </div>
      )}
      
      <button 
        className={styles.refreshButton}
        onClick={checkStatus}
        disabled={loading}
      >
        <FontAwesomeIcon icon={faSpinner} spin={loading} />
        {loading ? 'Checking...' : 'Refresh'}
      </button>
    </div>
  );
};
